import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Trophy, Heart, Zap, Volume2, Activity,
  History, ChevronUp, ChevronDown, CheckCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const COLORS = [
  { name: 'RED', hex: '#ef4444', bg: 'bg-red-500', hover: 'hover:bg-red-600' },
  { name: 'GREEN', hex: '#22c55e', bg: 'bg-green-500', hover: 'hover:bg-green-600' },
  { name: 'BLUE', hex: '#3b82f6', bg: 'bg-blue-500', hover: 'hover:bg-blue-600' },
  { name: 'YELLOW', hex: '#eab308', bg: 'bg-yellow-500', hover: 'hover:bg-yellow-600' }
];

// Assets hosted locally
const AUDIO_URLS = {
  RED: '/assets/audio/RED.mp3',
  GREEN: '/assets/audio/GREEN.mp3',
  BLUE: '/assets/audio/BLUE.mp3',
  YELLOW: '/assets/audio/YELLOW.mp3'
};

const BGM_URL = '/assets/audio/beat.mp3';

const STAGES = [
  { id: 1, instruction: "Tap the COLOR! not the word", type: "COLOR", rounds: 3, countdown: 5, duration: 3000 },
  { id: 2, instruction: "Tap the WORD! not the color", type: "WORD", rounds: 3, countdown: 5, duration: 3000 },
  { id: 3, instruction: "Tap the SOUND! ignore the screen", type: "AUDIO", rounds: 3, countdown: 5, duration: 3000 },
  { id: 4, instruction: "Tap the WORD! not what you hear", type: "WORD_AUDIO_CLASH", rounds: 3, countdown: 5, duration: 3000 },
  { id: 5, instruction: "Tap the COLOR! ignore everything else", type: "CHAOS_COLOR", rounds: 3, countdown: 5, duration: 3000 },
  { id: 6, instruction: "Now that you are warmed up...", type: "CHAOS_RANDOM", rounds: 10, countdown: 3, duration: 3000 },
  { id: 7, instruction: "Faster...", type: "CHAOS_RANDOM", rounds: 10, countdown: 3, duration: 2500 },
  { id: 8, instruction: "MAXIMUM OVERDRIVE", type: "CHAOS_RANDOM", rounds: 10, countdown: 3, duration: 2000 }
];

const TOTAL_LIVES = 3;

export default function BrainFrictionPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();
  
  // Game State
  const [gameState, setGameState] = useState('intro'); // intro, instruction, audio-check, playing, feedback, ended
  const [currentStageIdx, setCurrentStageIdx] = useState(0);
  const [currentRound, setCurrentRound] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(TOTAL_LIVES);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [countdown, setCountdown] = useState(5);
  
  // Stimulus State
  const [stimulus, setStimulus] = useState(null); // { text, colorIndex, audioIndex, correctIndex }
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', msg }
  
  // Data State
  const [leaderboard, setLeaderboard] = useState([]);
  const [attempts, setAttempts] = useState([]);

  // Refs
  const audioRefs = useRef({});
  const bgmRef = useRef(null);
  const timerRef = useRef(null);
  const roundTimerRef = useRef(null);
  const handleTimeoutRef = useRef(null);

  // State for Timer Visual
  const [textScale, setTextScale] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    
    // Preload audio
    Object.keys(AUDIO_URLS).forEach(key => {
      audioRefs.current[key] = new Audio(AUDIO_URLS[key]);
      audioRefs.current[key].volume = 1.0;
    });

    // Setup BGM
    const bgm = new Audio(BGM_URL);
    bgm.loop = true;
    bgm.volume = 0.5;
    bgmRef.current = bgm;
    
    // Attempt autoplay
    bgm.play().catch(e => console.log("Autoplay prevented, waiting for interaction"));
    
    fetchLeaderboard();
    fetchAttempts();
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (roundTimerRef.current) clearTimeout(roundTimerRef.current);
      bgm.pause();
      bgm.currentTime = 0;
    };
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/brain-friction/leaderboard`);
      setLeaderboard(response.data);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
    }
  };

  const fetchAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/brain-friction/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempts(response.data);
    } catch (e) {
      console.error('Failed to fetch attempts', e);
    }
  };

  const stopAllAudio = () => {
    Object.values(audioRefs.current).forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
  };

  const startGame = () => {
    stopAllAudio();
    // Ensure BGM is playing (if autoplay was blocked)
    if (bgmRef.current && bgmRef.current.paused) {
        bgmRef.current.play().catch(e => console.error("BGM play failed", e));
    }
    
    setScore(0);
    setLives(TOTAL_LIVES);
    setStreak(0);
    setMaxStreak(0);
    setCurrentStageIdx(0);
    setCurrentRound(1);
    startInstructionPhase(0);
  };

  const startInstructionPhase = (stageIdx) => {
    // Intercept Stage 3 for Audio Check
    if (stageIdx === 2) {
      setGameState('audio-check');
      setCurrentStageIdx(stageIdx);
      return;
    }

    runCountdown(stageIdx);
  };

  const runCountdown = (stageIdx) => {
    // Clear any existing timer to prevent overlaps
    if (timerRef.current) clearInterval(timerRef.current);

    setGameState('instruction');
    setCurrentStageIdx(stageIdx);
    setCountdown(STAGES[stageIdx].countdown);
    
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          startRound(stageIdx, 1);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    timerRef.current = interval;
  };

  const handleAudioCheckConfirm = () => {
    runCountdown(currentStageIdx);
  };

  const handleTimeout = () => {
    if (gameState !== 'playing') return;
    
    // Deduct life
    const newLives = lives - 1;
    setLives(newLives);
    setScore(prev => Math.max(0, prev - 1));
    setStreak(0);
    setFeedback({ type: 'error', msg: 'Too Slow!' });
    
    if (newLives <= 0) {
        setGameState('feedback');
        setTimeout(() => endGame(), 1000);
        return;
    }

    proceedToNext(currentRound, currentStageIdx);
  };

  // Update timeout ref
  useEffect(() => {
    handleTimeoutRef.current = handleTimeout;
  });

  const startRound = (stageIdx, roundNum) => {
    stopAllAudio();
    setGameState('playing');
    setFeedback(null);
    setCurrentStageIdx(stageIdx);
    setCurrentRound(roundNum);
    // Timer/Animation handled by useEffect
    
    generateStimulus(stageIdx);
  };

  // Handle Round Timer & Animation
  useEffect(() => {
    if (gameState === 'playing') {
      const stageDuration = STAGES[currentStageIdx].duration;

      // 1. Reset & Start Animation
      // Start larger and shrink significantly to be noticeable
      setTextScale(1.5);
      
      // Force a reflow/repaint before starting the transition
      const animFrame = requestAnimationFrame(() => {
        // Shrink to 0.5 over the duration of the stage
        const animTimeout = setTimeout(() => setTextScale(0.5), 100);
        
        // 2. Start Game Timer
        if (roundTimerRef.current) clearTimeout(roundTimerRef.current);
        roundTimerRef.current = setTimeout(() => {
          if (handleTimeoutRef.current) handleTimeoutRef.current();
        }, stageDuration);

        return () => clearTimeout(animTimeout);
      });

      return () => {
        cancelAnimationFrame(animFrame);
        if (roundTimerRef.current) clearTimeout(roundTimerRef.current);
      };
    }
  }, [gameState, currentRound, currentStageIdx]);

  const generateStimulus = (stageIdx) => {
    const stageType = STAGES[stageIdx].type;
    
    // Defaults
    let textIdx = Math.floor(Math.random() * 4);
    let colorIdx = -1;
    let audioIdx = -1;
    let correctIdx = 0;
    let activeRule = "";

    // Helper for 80% mismatch
    const getMismatchIndex = (baseIdx) => {
        if (Math.random() < 0.8) {
            let newIdx;
            do {
                newIdx = Math.floor(Math.random() * 4);
            } while (newIdx === baseIdx);
            return newIdx;
        }
        return baseIdx;
    };

    switch (stageType) {
        case 'COLOR':
            textIdx = Math.floor(Math.random() * 4);
            colorIdx = getMismatchIndex(textIdx);
            correctIdx = colorIdx;
            activeRule = "Tap the COLOR";
            break;

        case 'WORD':
            textIdx = Math.floor(Math.random() * 4);
            colorIdx = getMismatchIndex(textIdx);
            correctIdx = textIdx;
            activeRule = "Tap the WORD";
            break;

        case 'AUDIO':
            textIdx = Math.floor(Math.random() * 4);
            colorIdx = -1; // Purple
            audioIdx = getMismatchIndex(textIdx);
            correctIdx = audioIdx;
            activeRule = "Tap the SOUND";
            break;

        case 'WORD_AUDIO_CLASH':
            textIdx = Math.floor(Math.random() * 4); // Target
            colorIdx = -1; // Purple
            audioIdx = getMismatchIndex(textIdx); // Distractor
            correctIdx = textIdx;
            activeRule = "Tap the WORD";
            break;

        case 'CHAOS_COLOR':
            colorIdx = Math.floor(Math.random() * 4); // Target
            textIdx = getMismatchIndex(colorIdx); // Distractor 1
            audioIdx = getMismatchIndex(colorIdx); // Distractor 2
            correctIdx = colorIdx;
            activeRule = "Tap the COLOR";
            break;

        case 'CHAOS_RANDOM':
            // Randomly select task
            const tasks = ['COLOR', 'WORD', 'AUDIO'];
            const task = tasks[Math.floor(Math.random() * tasks.length)];
            
            // Generate full chaos stimuli
            textIdx = Math.floor(Math.random() * 4);
            colorIdx = Math.floor(Math.random() * 4);
            audioIdx = Math.floor(Math.random() * 4);
            
            if (task === 'COLOR') {
                correctIdx = colorIdx;
                activeRule = "Tap the COLOR";
            } else if (task === 'WORD') {
                correctIdx = textIdx;
                activeRule = "Tap the WORD";
            } else { // AUDIO
                correctIdx = audioIdx;
                activeRule = "Tap the SOUND";
            }
            break;
            
        default:
            break;
    }

    // Play Audio if needed
    if (audioIdx !== -1) {
        const audioKey = COLORS[audioIdx].name;
        const audio = audioRefs.current[audioKey];
        if (audio) {
            audio.currentTime = 0;
            audio.play().catch(e => console.error("Audio play failed", e));
        }
    }

    setStimulus({
        textIdx,
        colorIdx,
        audioIdx,
        correctIdx,
        activeRule
    });
  };

  const handleInput = (clickedIdx) => {
    if (gameState !== 'playing' || !stimulus) return;
    
    // Stop Timer
    if (roundTimerRef.current) clearTimeout(roundTimerRef.current);

    const isCorrect = clickedIdx === stimulus.correctIdx;
    
    if (isCorrect) {
        // Correct
        setScore(prev => prev + 1);
        setStreak(prev => {
            const newStreak = prev + 1;
            if (newStreak > maxStreak) setMaxStreak(newStreak);
            return newStreak;
        });
        setFeedback({ type: 'success', msg: 'Correct!' });
    } else {
        // Incorrect
        const newLives = lives - 1;
        setLives(newLives);
        setScore(prev => Math.max(0, prev - 1));
        setStreak(0);
        setFeedback({ type: 'error', msg: 'Missed!' });
        
        if (newLives <= 0) {
            setGameState('feedback');
            setTimeout(() => endGame(), 1000);
            return;
        }
    }

    proceedToNext(currentRound, currentStageIdx);
  };

  const proceedToNext = (round, stageIdx) => {
    // Delay before next round or stage
    setGameState('feedback');
    setTimeout(() => {
        // Use dynamic round count
        const maxRounds = STAGES[stageIdx].rounds;
        
        if (round < maxRounds) {
            // Next Round in same Stage
            startRound(stageIdx, round + 1);
        } else {
            // Stage Complete
            if (stageIdx < STAGES.length - 1) {
                // Next Stage
                startInstructionPhase(stageIdx + 1);
            } else {
                // Game Complete (All stages done)
                endGame(true);
            }
        }
    }, 2000); // 2 seconds between rounds
  };

  const endGame = async (completed = false) => {
    setGameState('ended');
    
    if (user) {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/brain-friction/results`, {
                score: score,
                max_streak: maxStreak,
                total_hits: completed ? (STAGES.length * ROUNDS_PER_STAGE) : 0, 
                avg_reaction_time: 0 
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Result saved!");
            fetchLeaderboard();
            fetchAttempts();
        } catch (e) {
            console.error("Failed to save result", e);
        }
    }
  };

  // Render Helpers
  const getStimulusColor = () => {
    if (!stimulus) return '#fff';
    if (stimulus.colorIdx === -1) return '#a855f7'; // Purple for Stage 3
    return COLORS[stimulus.colorIdx].hex;
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans selection:bg-white selection:text-black touch-none">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 p-4 flex justify-between items-center bg-[#020617]/90 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" /> Exit
            </Button>
            <div className="text-sm text-slate-400 hidden sm:block">
                {user?.username}
            </div>
        </div>
        <div className="flex gap-6 font-mono text-sm">
           <div className="flex items-center gap-2">
             <Trophy className="w-4 h-4 text-yellow-500" />
             <span className="text-xl font-bold">{score}</span>
           </div>
           <div className="flex items-center gap-2 text-red-500">
             <Heart className="w-4 h-4 fill-current" />
             <span className="text-xl font-bold">{lives}</span>
           </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Game Area */}
            <div className={`${isMobile ? 'w-full' : 'lg:col-span-2'}`}>
                <div className="flex flex-col items-center justify-center p-4 relative w-full bg-slate-900/30 rounded-3xl border border-slate-800/50 min-h-[400px] sm:min-h-[500px]">
                    
                    {/* Intro Screen */}
                    {gameState === 'intro' && (
                        <div className="text-center space-y-8 animate-in fade-in zoom-in duration-500">
                            <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-indigo-500/20">
                                <Activity className="w-12 h-12 text-white" />
                            </div>
                            <div>
                                <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">THE BRAIN FRICTION</h1>
                                <p className="text-slate-400 text-lg max-w-xs mx-auto">
                                    8 Stages. 45 Rounds. <br/>
                                    Fight the Interference.
                                </p>
                            </div>
                            <Button onClick={startGame} className="w-full py-8 text-xl font-bold bg-white text-black hover:bg-slate-200 rounded-xl uppercase tracking-wider shadow-xl shadow-white/10">
                                Start Challenge
                            </Button>
                        </div>
                    )}

                    {/* Audio Check Screen */}
                    {gameState === 'audio-check' && (
                        <div className="text-center space-y-8 animate-in fade-in zoom-in duration-500">
                            <div className="w-24 h-24 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto animate-pulse">
                                <Volume2 className="w-12 h-12 text-blue-400" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold mb-4">Sound Check Required</h2>
                                <p className="text-slate-400 text-lg max-w-xs mx-auto">
                                    The next stage requires audio cues. Please ensure your speakers are on.
                                </p>
                            </div>
                            <Button 
                                onClick={handleAudioCheckConfirm} 
                                className="w-full py-6 text-lg font-bold bg-blue-600 hover:bg-blue-500 rounded-xl"
                            >
                                <CheckCircle className="w-5 h-5 mr-2" />
                                I'm Listening
                            </Button>
                        </div>
                    )}

                    {/* Instruction Phase */}
                    {gameState === 'instruction' && (
                        <div className="text-center space-y-8 animate-in fade-in duration-300">
                            <div className="text-indigo-500 font-mono text-xl uppercase tracking-widest">Stage {currentStageIdx + 1}</div>
                            <h2 className="text-3xl sm:text-5xl font-black leading-tight max-w-sm mx-auto">
                                {STAGES[currentStageIdx].instruction}
                            </h2>
                            <div className="text-8xl font-black font-mono tabular-nums text-slate-700">
                                {countdown}
                            </div>
                        </div>
                    )}

                    {/* Gameplay Phase */}
                    {(gameState === 'playing' || gameState === 'feedback') && stimulus && (
                        <div className="w-full flex flex-col items-center gap-4 sm:gap-12 animate-in fade-in duration-300">
                            {/* Stage Indicator */}
                            <div className="flex items-center gap-2 text-slate-500 text-sm font-mono uppercase tracking-wider">
                                <span>Stage {currentStageIdx + 1}</span>
                                <span className="text-slate-700">/</span>
                                <span>Round {currentRound}</span>
                            </div>

                            {/* Stimulus Card */}
                            <div className="relative w-full h-32 sm:h-48 max-w-lg flex items-center justify-center bg-slate-900 rounded-3xl border-2 border-slate-800 shadow-2xl overflow-hidden">
                                {stimulus.activeRule && (
                                    <div className="absolute top-2 left-0 w-full text-center z-10 pointer-events-none">
                                        <div className="inline-flex items-baseline gap-2 bg-black/80 px-4 py-1.5 rounded-full border border-indigo-500/30 shadow-lg shadow-indigo-500/10">
                                            <span className="text-indigo-500 text-xs sm:text-sm font-medium uppercase tracking-widest">Tap the</span>
                                            <span className="text-white text-lg sm:text-xl font-black uppercase tracking-wider animate-pulse">
                                                {stimulus.activeRule.replace(/Tap (the|what you) /, '')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {gameState === 'feedback' && feedback ? (
                                    <div className={`text-4xl font-bold ${feedback.type === 'success' ? 'text-emerald-500' : 'text-red-500'} animate-in zoom-in duration-300`}>
                                        {feedback.msg}
                                    </div>
                                ) : (
                                    <div className="relative w-full h-full flex items-center justify-center">
                                        <div 
                                            className="text-8xl sm:text-9xl font-black tracking-tighter transition-transform ease-linear will-change-transform"
                                            style={{ 
                                                color: getStimulusColor(),
                                                textShadow: `0 0 40px ${getStimulusColor()}40`,
                                                transform: `scale(${textScale})`,
                                                transitionDuration: `${STAGES[currentStageIdx]?.duration || 3000}ms`
                                            }}
                                        >
                                            {COLORS[stimulus.textIdx].name}
                                        </div>
                                        
                                        {/* Audio Indicator */}
                                        {stimulus.audioIdx !== -1 && (
                                            <div className="absolute top-4 right-4 animate-pulse">
                                                <Volume2 className="w-6 h-6 text-slate-400" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Controls */}
                            <div className="grid grid-cols-2 gap-4 w-full max-w-[400px]">
                                {COLORS.map((color, idx) => (
                                    <button
                                        key={color.name}
                                        onClick={() => handleInput(idx)}
                                        disabled={gameState !== 'playing'}
                                        className={`
                                            h-20 sm:h-24 rounded-2xl font-bold text-xl tracking-wider transition-all transform active:scale-95
                                            ${color.bg} ${color.hover} text-white shadow-lg
                                            disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                                        `}
                                    >
                                        {color.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Game Over Screen */}
                    {gameState === 'ended' && (
                        <div className="text-center space-y-8 animate-in zoom-in duration-500">
                            <div className="text-6xl mb-4">
                                {lives > 0 ? '🎉' : '💀'}
                            </div>
                            <h2 className="text-4xl font-black">
                                {lives > 0 ? 'SEQUENCE COMPLETE' : 'GAME OVER'}
                            </h2>
                            
                            <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 w-full max-w-xs mx-auto space-y-4">
                                <div>
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Final Score</div>
                                    <div className="text-5xl font-mono font-bold text-white">{score}</div>
                                </div>
                                <div className="border-t border-slate-800 pt-4">
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Max Streak</div>
                                    <div className="text-2xl font-mono font-bold text-indigo-400">{maxStreak}</div>
                                </div>
                            </div>

                            <div className="flex gap-4 justify-center">
                                <Button onClick={startGame} className="py-6 px-8 text-lg font-bold bg-white text-black hover:bg-slate-200">
                                    Try Again
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Leaderboard */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Global Leaderboard</h3>
                </div>
                <ScrollArea className="h-64">
                  {leaderboard.length > 0 ? (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            entry.username === user?.username ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {entry.rank}
                            </span>
                            <span className="font-medium truncate max-w-24">{entry.username}</span>
                          </div>
                          <span className="font-mono font-bold text-indigo-400">
                            {Math.round(entry.score)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No entries yet. Be the first!
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recent Attempts */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-heading font-bold text-lg">Your History</h3>
                </div>
                <ScrollArea className="h-64">
                  {attempts.length > 0 ? (
                    <div className="space-y-2">
                      {attempts.slice(0, 15).map((attempt, idx) => {
                        const prevAttempt = attempts[idx + 1];
                        const isImprovement = prevAttempt && attempt.score > prevAttempt.score;
                        const isWorst = prevAttempt && attempt.score < prevAttempt.score;
                        
                        return (
                          <div 
                            key={attempt.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                          >
                            <div className="flex items-center gap-2">
                              {isImprovement && <ChevronUp className="w-4 h-4 text-emerald-400" />}
                              {isWorst && <ChevronDown className="w-4 h-4 text-red-400" />}
                              {!isImprovement && !isWorst && <div className="w-4" />}
                              <span className="text-slate-400 text-sm">
                                {new Date(attempt.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <span className="font-mono font-bold text-indigo-400">
                              {Math.round(attempt.score)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No attempts yet. Start testing!
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
